import { sportsNews } from '../../../mocks/sportsData';
import { newsService, NewsItem } from '../../../services/newsService';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNewsTags } from '@/services/news';

interface SportsNewsProps {
  selectedSport: {
    id: number;
    name: string;
    logo?: string;
    sort?: number;
    state?: number;
    add_time?: string;
    number?: number;
  };
  newsList: any[];
}

export default function SportsNews({ selectedSport, newsList = [] }: SportsNewsProps) {
  const navigate = useNavigate();
  const [currentBatch, setCurrentBatch] = useState(0);
  const [fetchedNews, setFetchedNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string>(''); // 新增标签筛选状态
  const [newsTags, setNewsTags] = useState<{ id: number; name: string }[]>([]);
  const itemsPerBatch = 6;

  // 去重函数 - 基于标题和发布时间
  const removeDuplicates = (newNews: NewsItem[], existingNews: NewsItem[]) => {
    const existingSet = new Set(
      existingNews.map(news => `${news.title}-${news.publishTime}`)
    );
    
    return newNews.filter(news => 
      !existingSet.has(`${news.title}-${news.publishTime}`)
    );
  };
  function decodeHTMLEntities(text: string) {
    const txt = document.createElement('textarea');
    txt.innerHTML = text;
    return txt.value;
  }
  // 自动采集新闻
  const fetchLatestNews = async () => {
    setIsLoading(true);
    try {
      console.log('📰 开始采集最新体育新闻...',newsList);
      const latestNews = newsList;
      setFetchedNews(latestNews);
      console.log('📰 采集到最新体育新闻:', latestNews.length, '条');
    } catch (error) {
      console.error('新闻采集失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 合并的更新功能 - 优先采集新闻，如果没有新闻则换一批
  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      // 采集10条最新新闻
      const newLatestNews = await newsService.fetchLatestNews(10);
      
      // 与现有新闻去重
      const uniqueNews = removeDuplicates(newLatestNews, fetchedNews);
      
      if (uniqueNews.length > 0) {
        // 将新的唯一新闻添加到现有新闻前面
        const updatedNews = [...uniqueNews, ...fetchedNews];
        setFetchedNews(updatedNews);
        console.log('🔄 更新成功，新增', uniqueNews.length, '条新闻，总计', updatedNews.length, '条');
        // 重置到第一批显示最新内容
        setCurrentBatch(0);
      } else {
        // 如果没有新的新闻，则执行换一批功能
        const filteredNews = getFilteredNews();
        const totalBatches = Math.ceil(filteredNews.length / itemsPerBatch);
        if (totalBatches > 1) {
          setCurrentBatch((prev) => (prev + 1) % totalBatches);
          console.log('📰 暂无新的新闻更新，显示下一批内容');
        } else {
          console.log('📰 暂无新的新闻更新');
        }
      }
    } catch (error) {
      console.error('新闻更新失败:', error);
      // 如果采集失败，尝试换一批
      const filteredNews = getFilteredNews();
      const totalBatches = Math.ceil(filteredNews.length / itemsPerBatch);
      if (totalBatches > 1) {
        setCurrentBatch((prev) => (prev + 1) % totalBatches);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 页面加载时自动采集
  useEffect(() => {
    fetchLatestNews();
  }, [JSON.stringify(newsList)]);

  // 页面初始化时请求新闻标签（支持接口返回对象数组）
  useEffect(() => {
    getNewsTags().then((res) => {
      // 兼容 res 为对象数组或字符串数组
      if (Array.isArray(res)) {
        if (typeof res[0] === 'object' && res[0]?.name) {
          setNewsTags(res as { id: number; name: string }[]);
        } else {
          // 字符串数组兼容
          setNewsTags((res as string[]).map((name, idx) => ({ id: idx + 1, name })));
        }
      } else if (res?.data && Array.isArray(res.data)) {
        setNewsTags(res.data);
      } else {
        setNewsTags([]);
      }
    }).catch(() => {
      setNewsTags([]);
    });
  }, []);
  
  // 获取当前显示的新闻源 - 优先显示采集的新闻
  const getCurrentNewsSource = () => {
    return fetchedNews.length > 0 ? fetchedNews : sportsNews;
  };
  
  // 根据选中的体育类型和标签筛选新闻（兼容多种数据格式）
  const getFilteredNews = () => {
    const newsSource = getCurrentNewsSource();
    let filtered = newsSource;

    // 按体育类型筛选（兼容 category 为对象或字符串或数字）
    if (selectedSport && selectedSport.name !== '全部') {
      filtered = filtered.filter(news => {
        if (typeof news.category === 'object' && news.category?.name) {
          return news.category.name === selectedSport.name;
        }
        if (typeof news.category === 'string') {
          return news.category === selectedSport.name;
        }
        if (typeof news.category === 'number' && news.category_id) {
          // 可根据 category_id 做映射
          return String(news.category_id) === String(selectedSport.id);
        }
        return false;
      });
    }

    // 按标签筛选（兼容 tags 为字符串或数组）
    if (selectedTag) {
      filtered = filtered.filter(news => {
        const tagsArr = Array.isArray(news.tags)
          ? news.tags
          : typeof news.tags === 'string'
            ? news.tags.split(',').map(t => t.trim())
            : [];
        return (
          news.title?.includes(selectedTag) ||
          news.summary?.includes(selectedTag) ||
          tagsArr.includes(selectedTag)
        );
      });
    }

    return filtered;
  };
  
  // 计算当前批次显示的新闻
  const getCurrentNews = () => {
    const filteredNews = getFilteredNews();
    const startIndex = currentBatch * itemsPerBatch;
    const endIndex = startIndex + itemsPerBatch;
    return filteredNews.slice(startIndex, endIndex);
  };
  
  // 当选中的体育类型或标签改变时，重置到第一批
  useEffect(() => {
    setCurrentBatch(0);
  }, [selectedSport, selectedTag]);

  // 处理标签点击
  const handleTagClick = (tag: string) => {
    if (selectedTag === tag) {
      // 如果点击的是已选中的标签，则取消选择
      setSelectedTag('');
    } else {
      // 选择新标签
      setSelectedTag(tag);
    }
  };
  
  // 处理新闻点击
  const handleNewsClick = (news: NewsItem) => {
    if (isShowingFetchedNews && news.url) {
      // 实时采集的新闻，在站内打开详情页
      navigate(`/newsDetail/${news.id}`);
    } else {
      // 本地新闻，直接跳转到详情页
      navigate(`/newsDetail/${news.id}`);
    }
  };

  const filteredNews = getFilteredNews();
  const currentNews = getCurrentNews();
  const totalBatches = Math.ceil(filteredNews.length / itemsPerBatch);
  const hasMoreBatches = totalBatches > 1;
  const isShowingFetchedNews = fetchedNews.length > 0;

  console.log('当前新闻列表:', newsList);
  return (
    <div className="w-full lg:w-64 lg:flex-shrink-0 mb-8 lg:mb-0">
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center">
            <i className="ri-newspaper-line text-green-600 mr-2"></i>
            体育新闻
            {selectedSport?.name !== '全部' && (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-600 rounded text-xs">
                {selectedSport?.name}
              </span>
            )}
            {selectedTag && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs flex items-center">
                #{selectedTag}
                <button
                  onClick={() => setSelectedTag('')}
                  className="ml-1 hover:text-blue-800"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            )}
          </h2>
          {/* 合并后的更新按钮 */}
          <button
            onClick={handleUpdate}
            disabled={isLoading}
            className={`flex items-center text-sm font-medium transition-colors cursor-pointer ${
              isLoading 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-green-600 hover:text-green-700'
            }`}
          >
            <i className={`mr-1 ${isLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`}></i>
            {isLoading ? '更新中' : '更新'}
          </button>
        </div>

        {/* 新闻来源信息 */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center mr-2">
                <i className="ri-news-line text-white text-xs"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">
                  {isShowingFetchedNews ? '实时采集' : '本地新闻'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">
                {filteredNews.length}条
                {selectedTag && <span className="ml-1">#{selectedTag}</span>}
              </p>
              <p className="text-xs text-green-500">
                {isShowingFetchedNews ? '采集时间' : '5小时前'}
              </p>
            </div>
          </div>
        </div>
        
        {/* 加载状态 */}
        {isLoading && (
          <div className="text-center py-6">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-loader-4-line text-green-500 text-lg animate-spin"></i>
            </div>
            <p className="text-green-600 text-sm">正在更新最新体育新闻...</p>
          </div>
        )}
        
            {currentNews.length === 0 && !isLoading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-newspaper-line text-gray-400 text-lg"></i>
                </div>
                <p className="text-gray-500 text-sm">暂无{selectedSport?.name}相关新闻</p>
              </div>
            ) : !isLoading && (
          <div className="space-y-4">
            {currentNews.map((news) => (
              <div
                key={news.id}
                onClick={() => handleNewsClick(news)}
                className="block p-4 rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-xs md:text-sm group-hover:text-green-600 transition-colors line-clamp-2 leading-relaxed flex-1">
                    {news.title}
                  </h3>
                  <i className="ri-arrow-right-line text-gray-400 text-xs ml-2 flex-shrink-0 group-hover:text-green-600 transition-colors"></i>
                </div>
                {/* 封面图展示 */}
                {/* {news.cover_img && (
                  <img
                    src={news.cover_img}
                    alt={news.title}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )} */}
                {/* 新闻内容摘要（优先 summary，没有则截取 content） */}
                <p className="text-xs md:text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                  {typeof news.summary === 'string'
                    ? news.summary
                    : typeof news.content === 'string'
                      ?decodeHTMLEntities(news.content || '').replace(/<[^>]+>/g, '').slice(0, 60) + '...'
                      : ''}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center min-w-0 flex-1">
                    <i className="ri-time-line mr-1 flex-shrink-0"></i>
                    <span className="truncate">{news.add_time ?? news.publishTime ?? ''}</span>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-600 rounded text-xs whitespace-nowrap flex-shrink-0 ml-2">
                    {typeof news.category === 'object' && news.category?.name
                      ? news.category.name
                      : typeof news.category === 'string'
                        ? news.category
                        : news.category_id ?? ''}
                  </span>
                </div>
                
              </div>
            ))}
          </div>
        )}
        
        {/* 批次指示器 */}
        {hasMoreBatches && !isLoading && (
          <div className="mt-4 flex items-center justify-center">
            <div className="flex space-x-1">
              {Array.from({ length: totalBatches }, (_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentBatch ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* 热门标签 */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm md:text-base">热门标签</h3>
          <div className="flex flex-wrap gap-2">
            {(newsTags.length > 0 ? newsTags : [{ name: '转会' }, { name: '篮球' }, { name: '足球' }, { name: '世界杯' }, { name: '奥运会' }, { name: '网球' }, { name: '排球' }]).map((tag) => (
              <span
                key={tag.name}
                onClick={() => handleTagClick(tag.name)}
                className={`px-3 py-1 text-xs rounded-full cursor-pointer transition-colors ${
                  selectedTag === tag.name
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-green-100 text-gray-700 hover:text-green-600'
                }`}
              >
                #{tag.name}
              </span>
            ))}
          </div>
          {selectedTag && (
            <div className="mt-3 p-2 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-600">
                  正在显示 "#{selectedTag}" 相关新闻
                </span>
                <button
                  onClick={() => setSelectedTag('')}
                  className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  清除筛选
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* 更多新闻 */}
        <div className="mt-4">
          <a
            href="/news"
            className="block w-full py-2 text-center text-sm text-green-600 hover:text-green-700 font-medium border border-green-200 hover:border-green-300 rounded-lg transition-colors"
          >
            查看更多新闻
            <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
